import { Banner, BannerDescription, BannerTitle } from "@prismicio/editor-ui"
import { captureException } from "@sentry/react"
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"

import { useGlobalBanner } from "../../app/banners/GlobalBanner"

type LiveblocksError = {
  type: "connection" | "authentication"
  message: string
}

type LiveblocksErrorContextValue = {
  error: LiveblocksError | null
  setError: (error: LiveblocksError) => void
  clearError: () => void
  hasError: boolean
}

const LiveblocksErrorContext = createContext<
  LiveblocksErrorContextValue | undefined
>(undefined)

export function LiveblocksErrorProvider({ children }: { children: ReactNode }) {
  const [error, setErrorState] = useState<LiveblocksError | null>(null)
  const { showBanner } = useGlobalBanner()

  const setError = useCallback(
    (error: LiveblocksError) => {
      setErrorState(error)
      captureException(error)
      showBanner({
        id: `liveblocks-error-${Date.now()}`,
        render: LiveblocksErrorBanner,
      })
    },
    [showBanner],
  )

  const clearError = useCallback(() => {
    setErrorState(null)
  }, [])

  const hasError = error !== null

  const value = useMemo(
    () => ({
      error,
      setError,
      clearError,
      hasError,
    }),
    [error, setError, clearError, hasError],
  )

  return (
    <LiveblocksErrorContext.Provider value={value}>
      {children}
    </LiveblocksErrorContext.Provider>
  )
}

export function useLiveblocksError() {
  const context = useContext(LiveblocksErrorContext)
  if (context === undefined) {
    throw new Error(
      "useLiveblocksError must be used within a LiveblocksErrorProvider",
    )
  }
  return context
}

interface LiveblocksErrorBannerProps {
  onClose: () => void
}

function LiveblocksErrorBanner(props: LiveblocksErrorBannerProps) {
  const { onClose } = props

  return (
    <Banner color="warn" iconName="alert" onClose={onClose}>
      <BannerTitle>
        Real-time collaboration is currently unavailable.
      </BannerTitle>
      <BannerDescription>
        Status indicator, comments, and auto-save are disabled. Please use the
        Save button to manually save your changes.
      </BannerDescription>
    </Banner>
  )
}
